import { useMemo } from "react";

/**
 * Lightweight SVG candlestick chart with trade-plan overlays.
 * Price axis is exact — no distortion. 2D professional mode.
 */
export function CandleChart({ candles = [], overlays = {}, height = 420 }) {
  const W = 1000;
  const H = height;
  const padR = 74; // right price gutter
  const padT = 16;
  const padB = 20;

  const view = useMemo(() => {
    if (!candles.length) return null;
    const data = candles.slice(-120);
    let lo = Math.min(...data.map((c) => c.l));
    let hi = Math.max(...data.map((c) => c.h));
    const extra = [];
    if (overlays.stop) extra.push(overlays.stop);
    if (overlays.tps) overlays.tps.forEach((t) => t && extra.push(t));
    if (overlays.entryZone) overlays.entryZone.forEach((t) => extra.push(t));
    if (overlays.prevDay) { extra.push(overlays.prevDay.high); extra.push(overlays.prevDay.low); }
    if (overlays.sessionRange) { extra.push(overlays.sessionRange.high); extra.push(overlays.sessionRange.low); }
    if (overlays.livePrice != null) extra.push(overlays.livePrice);
    (overlays.levels || []).forEach((l) => extra.push(l.price));
    extra.forEach((v) => { if (v < lo) lo = v; if (v > hi) hi = v; });
    const pad = (hi - lo) * 0.08 || 1;
    lo -= pad; hi += pad;
    const plotW = W - padR;
    const plotH = H - padT - padB;
    const x = (i) => (i / (data.length - 1 || 1)) * (plotW - 20) + 10;
    const y = (p) => padT + (1 - (p - lo) / (hi - lo)) * plotH;
    const cw = Math.max(2, (plotW - 20) / data.length * 0.6);
    return { data, lo, hi, x, y, cw, plotW };
  }, [candles, overlays, H]);

  if (!view) {
    return <div className="flex items-center justify-center text-[var(--text-muted)] text-sm" style={{ height }}>WAITING FOR DATA…</div>;
  }
  const { data, x, y, cw, plotW, lo, hi } = view;

  const priceTicks = [];
  const steps = 6;
  for (let i = 0; i <= steps; i++) priceTicks.push(lo + (hi - lo) * (i / steps));

  const emaPath = overlays.ema && overlays.ema.length === data.length
    ? overlays.ema.map((v, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`).join(" ")
    : null;

  const dir = overlays.direction;
  const tpColor = "#21C58E";

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: "block" }}>
      {/* grid */}
      {priceTicks.map((p, i) => (
        <g key={i}>
          <line x1="0" x2={plotW} y1={y(p)} y2={y(p)} stroke="var(--border-subtle)" strokeWidth="1" />
          <text x={plotW + 6} y={y(p) + 3} fontSize="11" fill="var(--text-muted)" className="tnum">{p.toFixed(2)}</text>
        </g>
      ))}

      {/* S/R levels */}
      {(overlays.levels || []).map((l, i) => (
        <g key={`lv${i}`}>
          <line x1="0" x2={plotW} y1={y(l.price)} y2={y(l.price)}
            stroke={l.kind === "support" ? "#21C58E" : "#EF6262"} strokeWidth="1" strokeDasharray="4 4" opacity="0.35" />
        </g>
      ))}

      {/* Previous day high / low (PDH / PDL) */}
      {overlays.prevDay && [["high", "PDH"], ["low", "PDL"]].map(([k, lbl]) => {
        const p = overlays.prevDay[k];
        if (p == null) return null;
        return (
          <g key={lbl}>
            <line x1="0" x2={plotW} y1={y(p)} y2={y(p)} stroke="#60A5FA" strokeWidth="1" strokeDasharray="2 3" opacity="0.75" />
            <rect x="0" y={y(p) - 8} width="34" height="16" rx="3" fill="#60A5FA" />
            <text x="17" y={y(p) + 3} fontSize="9" fill="#07090C" textAnchor="middle" fontWeight="700">{lbl}</text>
          </g>
        );
      })}

      {/* entry zone band */}
      {overlays.entryZone && (
        <rect x="0" width={plotW}
          y={y(Math.max(...overlays.entryZone))}
          height={Math.abs(y(Math.min(...overlays.entryZone)) - y(Math.max(...overlays.entryZone)))}
          fill="rgba(214,174,87,0.14)" stroke="#D6AE57" strokeWidth="1" strokeDasharray="3 3" />
      )}

      {/* stop */}
      {overlays.stop && (
        <g>
          <line x1="0" x2={plotW} y1={y(overlays.stop)} y2={y(overlays.stop)} stroke="#EF6262" strokeWidth="1.4" />
          <rect x={plotW - 52} y={y(overlays.stop) - 8} width="52" height="16" rx="3" fill="#EF6262" />
          <text x={plotW - 26} y={y(overlays.stop) + 3} fontSize="10" fill="#07090C" textAnchor="middle" fontWeight="700">STOP</text>
        </g>
      )}

      {/* take profits */}
      {(overlays.tps || []).map((tp, i) => tp && (
        <g key={`tp${i}`}>
          <line x1="0" x2={plotW} y1={y(tp)} y2={y(tp)} stroke={tpColor} strokeWidth="1.2" strokeDasharray="6 3" opacity="0.8" />
          <rect x={plotW - 52} y={y(tp) - 8} width="52" height="16" rx="3" fill={tpColor} />
          <text x={plotW - 26} y={y(tp) + 3} fontSize="10" fill="#07090C" textAnchor="middle" fontWeight="700">TP{i + 1}</text>
        </g>
      ))}

      {/* EMA */}
      {emaPath && <path d={emaPath} fill="none" stroke="#D6AE57" strokeWidth="1.4" opacity="0.85" />}

      {/* Asian-session (Tokyo) range box */}
      {overlays.sessionRange && (
        <g>
          <rect x="0" y={y(overlays.sessionRange.high)} width={plotW}
            height={Math.max(1, y(overlays.sessionRange.low) - y(overlays.sessionRange.high))}
            fill="rgba(139,92,246,0.10)" stroke="rgba(139,92,246,0.55)" strokeWidth="1" strokeDasharray="3 3" />
          <rect x="2" y={y(overlays.sessionRange.high) - 7} width="62" height="14" rx="3" fill="rgba(139,92,246,0.92)" />
          <text x="33" y={y(overlays.sessionRange.high) + 3} fontSize="8.5" fill="#fff" textAnchor="middle" fontWeight="700">ASIA RANGE</text>
        </g>
      )}

      {/* candles */}
      {data.map((c, i) => {
        const up = c.c >= c.o;
        const col = up ? "#21C58E" : "#EF6262";
        const bodyTop = y(Math.max(c.o, c.c));
        const bodyH = Math.max(1, Math.abs(y(c.o) - y(c.c)));
        return (
          <g key={i}>
            <line x1={x(i)} x2={x(i)} y1={y(c.h)} y2={y(c.l)} stroke={col} strokeWidth="1" />
            <rect x={x(i) - cw / 2} y={bodyTop} width={cw} height={bodyH} fill={col} rx="0.5" />
          </g>
        );
      })}

      {/* live current price */}
      {overlays.livePrice != null && (
        <g>
          <line x1="0" x2={plotW} y1={y(overlays.livePrice)} y2={y(overlays.livePrice)}
            stroke="#EAB308" strokeWidth="1" strokeDasharray="1 3" opacity="0.9" />
          <circle cx={x(data.length - 1)} cy={y(overlays.livePrice)} r="3.5" fill="#EAB308">
            <animate attributeName="opacity" values="1;0.2;1" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <rect x={plotW - 52} y={y(overlays.livePrice) - 8} width="52" height="16" rx="3" fill="#EAB308" />
          <text x={plotW - 26} y={y(overlays.livePrice) + 3} fontSize="10" fill="#07090C" textAnchor="middle" fontWeight="700">{overlays.livePrice.toFixed(2)}</text>
        </g>
      )}
    </svg>
  );
}
